import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faXmarkCircle,
  faSpinner
} from '@fortawesome/free-solid-svg-icons'
import useAxiosPrivateSAI from '../../../../hooks/useAxiosPrivateSAI'
import useAuth from '../../../../hooks/useAuth'
import StepTwoEdit from '../Partials/StepTwoEdit'
import StepThree from '../Partials/StepThree'
import ClaimConfirmation from '../ClaimConfirmation'
import moment from 'moment'
import { useTheme } from '../../../../context/ThemeProvider'
import StepOneEdit from '../Partials/StepOneEdit'
import {toast} from 'sonner'
import CompletedClaim from '../Partials/CompletedClaim'
import EditClaimConfirmation from '../EditClaimConfirmation'

const EditClaimSlideOut = ({ toggleClaim, setToggleClaim, claim = {}, policy ={}, onRefresh }) => {
  const theme = useTheme()
  const axios = useAxiosPrivateSAI()
  const { setShowLoader} = useAuth()
  const style = {
    backgroundColor: theme.backgroundColor,
    color: theme.textColor,
  }
  const [step, setStep] = useState(1)
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [accepted, setAccepted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [isStepOneValidState, setIsStepOneValidState] = useState(false)
  const [stepOneErrors, setStepOneErrors] = useState({})
  const [status, setStatus] = useState(null)
  const [formData, setFormData] = useState({
    stepOne: {},
    stepTwo: {},
    stepThree: {},
  })
  const nextStep = () => setStep((prev) => Math.min(prev + 1, 4));

  const handleClose = () => {
    console.log('here');
    setStep(1)
    setFormData({
      stepOne: {},
      stepTwo: {},
      stepThree: {},
    });
    setStepOneErrors({})
    setIsStepOneValidState(false)
    setLoading(false)
    setSubmitted(false)
    setToggleClaim(false)
  }

  useEffect(() => {
    const fetchClaimData = async () => {
      if (!claim?.claim_id || !policy?.policy_id) return
      if (formData?.stepOne?.claim_id === claim.claim_id) return
      if (formData?.stepTwo?.claim_id === claim.claim_id) return

      setShowLoader(true);
      try {
        const res = await axios.get(`/api/claims/current/${policy.policy_id}/${claim.claim_id}`);
        const current = res.data;
        const {
          claim_absence_start_date,
          claim_absence_end_date,
          claim_phased,
          shielding,
          confidential,
          claim_information,
          condition_id,
          condition,
          staff,
          declaration_signed,
          conditions
        } = current.claim;
  
        const parsedClaimInfo = JSON.parse(claim_information || '{}');
  
        setFormData({
          stepOne: {
            selectedStaff: staff || {},
            selectedCondition: {
              condition_id: conditions?.condition_id || condition_id,
              condition_title: conditions?.condition_title || condition,
              condition_phased: conditions?.condition_phased ?? 0,
            },
            absenceStart: claim_absence_start_date || null,
            absenceEnd: claim_absence_end_date || null,
            isPhasedReturn: !!claim_phased,
            isShielding: !!shielding,
            isConfidential: !!confidential,
            claim_id: current.claim.claim_id
          },
          stepTwo: {
            model: {
              continuous_absence: parsedClaimInfo.continuous_absence || null,
              twenty_working_days: parsedClaimInfo.twenty_working_days || null,
              parental_leave_planned: parsedClaimInfo.parental_leave_planned || null,
              upcoming_disciplinary_action: parsedClaimInfo.upcoming_disciplinary_action || null,
              supporting_documentation: parsedClaimInfo.supporting_documentation || null,
              relating_to: parsedClaimInfo.relating_to || null,
            },
          },
          stepThree: {
            accepted: !!declaration_signed,
          },
        })
      } catch (err) {
        console.error("Failed to load current claim data", err);
      } finally {
        setShowLoader(false);
      }
    };
  
    fetchClaimData();
  }, [claim?.claim_id, policy?.policy_id]);    

  useEffect(() => {
    const data = formData.stepOne;
    const isValid = checkStepOneValidity(data);
    setIsStepOneValidState(isValid);
    const errors = {};
    if (!data?.selectedStaff || !data?.selectedCondition || !data?.absenceStart) {
      errors.missingFields = 'All required fields must be completed.';
    } else if (!data.selectedStaff.maternityCover &&
      data.selectedCondition.condition_id === 'e3afcdea-7b54-11e7-a963-336a7d328401') {
      errors.invalidCondition = 'Maternity condition not allowed for this staff category.';
    } else if (
      moment(data.absenceStart).isAfter(moment()) &&
      data.selectedCondition.future_claims !== 1
    ) {
      errors.startDate = 'The first date of absence must be in the past.';
    } else if (data.absenceEnd) {
      const start = moment(data.absenceStart);
      const end = moment(data.absenceEnd);
      if (end.isBefore(start)) {
        errors.endDate = 'The return date must be after the start date.';
      } else if (end.isAfter(moment()) && data.selectedCondition.future_claims !== 1) {
        errors.endDate = 'The return to work date must be in the past.';
      }
    }
  
    setStepOneErrors(errors);
  }, [
    formData.stepOne.selectedStaff,
    formData.stepOne.selectedCondition,
    formData.stepOne.isPhasedReturn,
    formData.stepOne.absenceStart,
    formData.stepOne.absenceEnd
  ]);

  const checkStepOneValidity = (data) => {
    if (!data || !data.selectedCondition || !data.absenceStart) return false;
  
    const { selectedStaff, selectedCondition, absenceStart, absenceEnd } = data;
  
    if (!selectedStaff.staff_id || !selectedCondition.condition_id) return false;
    if (!selectedStaff.stress && selectedCondition.stress === 0) return false;

    if (
      !selectedStaff.maternityCover &&
      selectedCondition.condition_id === 'e3afcdea-7b54-11e7-a963-336a7d328401'
    ) return false;
  
    const start = moment(absenceStart)
    const end = absenceEnd ? moment(absenceEnd) : null
    const today = moment()
    const futureClaimsAllowed = selectedCondition.future_claims === 1;

    if (start.isAfter(today) && !futureClaimsAllowed) return false;

    if (end) {
      if (end.isBefore(start)) return false;
      if (end.isAfter(today) && !futureClaimsAllowed) return false;
    }

    if (end) {
      if (end.isBefore(start)) return false
      if (end.isAfter(today) && selectedCondition.future_claims !== 1) return false
    }
  
    return true
  }
  
  const isStepTwoValid = () => {
    const model = formData.stepTwo?.model || {}
    return (
      model.continuous_absence &&
      model.twenty_working_days &&
      model.parental_leave_planned &&
      model.upcoming_disciplinary_action &&
      model.supporting_documentation
    )
  }  
  
  const isStepThreeValid = () => {
    const data = formData.stepThree;
    setAccepted(data?.accepted);

    return data?.accepted === true;
  }
  
  const isCurrentStepValid = () => {
    switch (step) {
      case 1:
        return isStepOneValidState
      case 2:
        return isStepTwoValid();
      case 3:
        return isStepThreeValid();
      default:
        return false;
    }
  }  

  const handleNext = async () => {
    if (!isCurrentStepValid()) return
  
    setLoading(true)
  
    try {
      switch (step) {
        case 1:
          await submitStepOne()
          break;
        case 2:
          await submitStepTwo()
          break;
        case 3:
          await submitStepThree()
          break;
        default:
          break;
      }
    } finally {
      setLoading(false)
    }
  }

  const handleStepTwoUpdate = (stepTwoUpdate) => {
    setFormData(prev => ({
      ...prev,
      stepTwo: {
        ...prev.stepTwo,
        model: {
          ...prev.stepTwo.model,
          ...stepTwoUpdate.model,
        },
      },
    }))
  }

  const submitStepOne = async () => {
    const {
      selectedStaff,
      selectedCondition,
      absenceStart,
      absenceEnd,
      isPhasedReturn,
      isShielding,
      isConfidential,
    } = formData.stepOne;
  
    const claimId = formData.stepOne.claim_id || claim?.claim_id;

    if (!selectedStaff?.staff_id || !selectedCondition?.condition_id || !absenceStart) {
      toast.error("Missing required fields for Step One submission.");
      return;
    }
  
    const payload = {
      staff_id: selectedStaff.staff_id,
      claim_id: claimId || 0,
      condition_id: selectedCondition.condition_id,
      policy_id: policy.policy_id,
      claim_absence_start_date: absenceStart,
      claim_absence_end_date: absenceEnd || null,
      claim_phased: isPhasedReturn ? 1 : 0,
      shielding: isShielding ? 1 : 0,
      confidential: isConfidential ? 1 : 0
    }
  
    try {
      if (claim?.claim_id) {
        // await axios.put(`/api/claims/${claimId}`, payload)
        toast.success("Claim updated successfully.")
        nextStep()
      } else {
        const res = await axios.post('/api/claims', payload)
        if (res?.data?.claim_id) {
          const newClaim = res.data
  
          setFormData((prev) => ({
            ...prev,
            stepOne: {
              ...prev.stepOne,
              claim_id: newClaim.claim_id
            }
          }))
  
          toast.success("Claim created successfully.")
          nextStep();
        } else {
          toast.error("Claim created, but no claim id returned.")
        }
        nextStep();
      }
    } catch (err) {
      console.error("Step One error:", err)
      toast.error("Failed to submit Step One.")
    }
  }

  const submitStepTwo = async () => {
    const claimId = claim?.claim_id || formData?.stepOne?.claim_id
    const stepTwo = formData?.stepTwo
  
    if (!claimId || !stepTwo) {
      console.warn("Missing claim ID or step two data.")
      return
    }

    const {
      model = {}
    } = stepTwo;
    
    const {
      continuous_absence: continuous,
      twenty_working_days: contracted,
      parental_leave_planned: pregnant,
      upcoming_disciplinary_action: suspended,
      supporting_documentation: request,
      relating_to: relatingTo
    } = model;
  
    console.log('step', stepTwo)

    if (
      continuous == null ||
      contracted == null ||
      pregnant == null ||
      suspended == null ||
      request == null
    ) {
      console.warn("Incomplete Step Two data.")
      return
    }
  
    const claim_information = JSON.stringify(model)
  
    const payload = {
      claim_information,
      naht_member: "",
    }
  
    try {
      const res = await axios.put(`/api/claims/${claimId}`, payload)
      if (res.status === 200 || res.status === 201 || res.status === 204) {
        toast.success("Step Two successfully saved.")
        nextStep()
      } else {
        toast.error("Unexpected response while submitting Step Two.")
      }
    } catch (err) {
      toast.error("Failed to submit Step Two", err)
    }
  }

  const submitStepThree = async () => {
    const claimId = claim?.claim_id || formData?.stepOne?.claim_id
  
    if (!claimId) {
      toast.warning("No claim ID available to calculate the claim.")
      return
    }
  
    try {
      setSubmitting(true)
      const res = await axios.get(`/api/claims/calculate/${claimId}`)
      if (res.status === 200) {
        toast.success("Claim successfully calculated.")
        nextStep()
        setSubmitted(true) 
        if (onRefresh) onRefresh()
      } else {
        toast.error("Unexpected response from calculate endpoint.")
      }
    } catch (err) {
      toast.error("Failed to calculate claim", err)
    }
    setSubmitting(false)
  }
  
  return (
    <Dialog open={toggleClaim} onClose={handleClose} className="relative z-[100]">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition duration-500 ease-in-out data-[closed]:opacity-0"
      />
      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
            <DialogPanel
              transition
              className="pointer-events-auto w-screen max-w-2xl transform transition duration-500 ease-in-out data-[closed]:translate-x-full sm:duration-700"
            >
              <div className="flex h-full flex-col overflow-y-scroll bg-white shadow-xl">
                <div className=" px-4 py-6 sm:px-6"  style={{ backgroundColor: style.backgroundColor }}>
                  <div className="flex items-center justify-between">
                    <div>
                      <DialogTitle className="text-lg font-semibold leading-6 text-white">
                        View Claim {claim.claim_reference}
                      </DialogTitle>
                    </div>
                    <div className="ml-3 flex h-7 items-center">
                      <button
                        type="button"
                        className="relative text-white hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                        onClick={() => {
                          handleClose()
                        }}
                      >
                        <span className="absolute -inset-2.5" />
                        <span className="sr-only">Close panel</span>
                        <FontAwesomeIcon icon={faXmarkCircle} />
                      </button>
                    </div>
                  </div>
                </div>
                {step >= 1 && step <= 3 && (
                  <div className="p-6">
                  {step === 1 && (
                    <>
                      <StepOneEdit
                      claim={claim}
                      policyId={policy?.policy_id}
                      data={formData.stepOne}
                      onUpdate={(updated) =>
                        setFormData((prev) => ({
                          ...prev,
                          stepOne: { ...prev.stepOne, ...updated },
                        }))
                      }
                    />

                    {/* <CompletedClaim claim={claim}/> */}
                    </>
                  )}
                  {step === 2 && (
                    <StepTwoEdit
                      key={claim?.claim_id}
                      claim={claim}
                      policy={policy}
                      data={formData.stepTwo}
                      conditionId={claim?.condition_id}
                      onUpdate={handleStepTwoUpdate}
                    />
                  )}
                  {step === 3 && (
                    <StepThree
                      claim={claim}
                      data={formData.stepThree}
                      onUpdate={(data) =>
                        setFormData((prev) => ({ ...prev, stepThree: data }))
                      }
                    />
                  )}

                  {step === 4 && (
                    <EditClaimConfirmation
                      claim={claim}
                      data={formData.stepThree}
                      onUpdate={(data) =>
                        setFormData((prev) => ({ ...prev, stepThree: data }))
                      }
                    />
                  )}

                    <div className="flex justify-between mt-8">
                      {step < 3 ? (
                        loading ? (
                          <button
                            disabled
                            className="px-4 py-2 text-sm font-medium text-white bg-sas rounded-md flex items-center justify-center"
                          >
                            <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                            Loading...
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={handleNext}
                            disabled={!isCurrentStepValid()}
                            className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                              isCurrentStepValid() ? 'bg-sas hover:bg-sas-dark' : 'bg-gray-400 cursor-not-allowed'
                            }`}
                          >
                            Next Step
                          </button>
                        )
                      ) : (
                        <>
                        {formData.stepThree?.accepted && (
                          <button onClick={submitStepThree} disabled={submitting}  className="px-4 py-2 text-sm font-medium text-white bg-sas rounded-md hover:bg-sas-dark">
                            {submitting ? 'Submitting...' : 'Submit'}
                          </button>
                        )}
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Step 4: Confirmation */}
                {step === 4 && <EditClaimConfirmation />}
              </div>
            </DialogPanel>
          </div>
        </div>
      </div>
    </Dialog>
  )
}

export default EditClaimSlideOut
